import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ShieldCheck, ArrowLeft, Save, Loader2, CheckCircle, Users, Plus, Search, Eye, XCircle } from "lucide-react";
import { MarketProfileForm } from "@/components/forms/MarketProfileForm";
import { AdminStatusCard } from "@/components/admin/AdminStatusCard";
import { BusinessDetailModal } from "@/pages/DirectoryPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getLogoUrl = (logo) => {
  if (!logo) return null;
  if (logo.startsWith("http")) return logo;
  return `${BACKEND_URL}${logo}`;
};

const toSearchableList = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
};

const usableIdentifier = (value) => {
  const text = String(value || "").trim();
  return text && !["undefined", "null", "none"].includes(text.toLowerCase()) ? text : "";
};

const vendorIdentifier = (business) => (
  usableIdentifier(business?.id) ||
  usableIdentifier(business?._id) ||
  usableIdentifier(business?.business_email)
);

const AdminMarketEditPage = () => {
  const { marketId } = useParams();
  const isNew = marketId === "new";
  const navigate = useNavigate();
  const token = localStorage.getItem("bizkeep_admin_token");

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [vendorRequests, setVendorRequests] = useState([]);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [vendorRequestStatus, setVendorRequestStatus] = useState("pending");
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [loadingAvailableVendors, setLoadingAvailableVendors] = useState(false);
  const [addingVendorId, setAddingVendorId] = useState("");
  const [updatingVendorRequest, setUpdatingVendorRequest] = useState("");
  const [vendorSearchTerm, setVendorSearchTerm] = useState("");
  const [selectedVendorBusiness, setSelectedVendorBusiness] = useState(null);
  const [market, setMarket] = useState({
    market_name: "", email: "", website: "", address: "", location: "",
    season_start: "", season_end: "", season_year_round: false,
    hours: {}, hours_event_based: false, instagram: "", facebook: "",
    tiktok: "", logo: "", description: "",
  });

  const vendorRequestCounts = useMemo(() => ({
    pending: vendorRequests.filter((request) => request.status === "pending").length,
    approved: vendorRequests.filter((request) => request.status === "approved").length,
    rejected: vendorRequests.filter((request) => request.status === "rejected").length,
  }), [vendorRequests]);

  const filteredVendorRequests = useMemo(
    () => vendorRequests.filter((request) => request.status === vendorRequestStatus),
    [vendorRequests, vendorRequestStatus]
  );

  const filteredAvailableVendors = useMemo(() => {
    const search = vendorSearchTerm.trim().toLowerCase();
    if (!search) return availableVendors;
    return availableVendors.filter((business) => (
      business.business_name?.toLowerCase().includes(search) ||
      business.business_email?.toLowerCase().includes(search) ||
      business.business_type?.toLowerCase().includes(search) ||
      business.business_description?.toLowerCase().includes(search) ||
      toSearchableList(business.business_locations).some((location) => String(location).toLowerCase().includes(search)) ||
      toSearchableList(business.business_categories).some((category) => String(category).toLowerCase().includes(search))
    ));
  }, [availableVendors, vendorSearchTerm]);

  const fetchMarket = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/markets`, { headers: { Authorization: `Bearer ${token}` } });
      const found = r.data.find((m) => m.id === marketId);
      if (!found) {
        toast.error("Market not found");
        navigate("/admin/dashboard");
        return;
      }
      setMarket(found);
    } catch {
      toast.error("Failed to load market");
      navigate("/admin/dashboard");
    } finally { setLoading(false); }
  }, [marketId, token, navigate]);

  useEffect(() => {
    if (!token) { navigate("/admin"); return; }
    if (!isNew) fetchMarket();
  }, [token, isNew, navigate, fetchMarket]);

  const fetchVendorRequests = useCallback(async () => {
    if (isNew) return;
    try {
      const response = await axios.get(`${API}/admin/market/${encodeURIComponent(marketId)}/vendor-requests`, { headers: { Authorization: `Bearer ${token}` } });
      setVendorRequests(response.data || []);
    } catch {
      setVendorRequests([]);
    }
  }, [isNew, marketId, token]);

  useEffect(() => {
    if (!token || isNew) return;
    fetchVendorRequests();
  }, [token, isNew, fetchVendorRequests]);

  const fetchAvailableVendors = useCallback(async () => {
    if (isNew) return;
    setLoadingAvailableVendors(true);
    try {
      const response = await axios.get(`${API}/admin/market/${encodeURIComponent(marketId)}/available-vendors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableVendors(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load businesses");
      setAvailableVendors([]);
    } finally {
      setLoadingAvailableVendors(false);
    }
  }, [isNew, marketId, token]);

  const openAddVendorModal = () => {
    setVendorSearchTerm("");
    setShowAddVendorModal(true);
    fetchAvailableVendors();
  };

  const handleAddVendor = async (business) => {
    const businessId = vendorIdentifier(business);
    setAddingVendorId(businessId);
    try {
      const requestConfig = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { business_id: businessId, business };
      try {
        await axios.post(`${API}/admin/market/${encodeURIComponent(marketId)}/vendors`, payload, requestConfig);
      } catch (error) {
        if (error.response?.status !== 404) throw error;
        await axios.post(`${API}/admin/market/${encodeURIComponent(marketId)}/vendors/${encodeURIComponent(businessId)}`, { business }, requestConfig);
      }
      toast.success("Business added to market");
      setAvailableVendors((previous) => previous.map((business) => (
        business.id === businessId ? { ...business, vendor_status: "approved" } : business
      )));
      await fetchVendorRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add vendor");
    } finally {
      setAddingVendorId("");
    }
  };

  const handleVendorRequestAction = async (businessId, status) => {
    setUpdatingVendorRequest(`${businessId}-${status}`);
    try {
      await axios.post(
        `${API}/admin/market/${encodeURIComponent(marketId)}/vendor-requests/${encodeURIComponent(businessId)}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(status === "approved" ? "Vendor approved" : "Vendor marked not approved");
      await fetchVendorRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update vendor request");
    } finally {
      setUpdatingVendorRequest("");
    }
  };

  const handleChange = (field, value) => {
    setMarket((p) => ({ ...p, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (isNew && !market.market_name) {
      toast.error("Market name is required");
      return;
    }
    if (isNew && !market.email) {
      toast.error("Market email is required");
      return;
    }
    setSaving(true);
    try {
      const updateData = {
        market_name: market.market_name, email: market.email, website: market.website, address: market.address,
        location: market.location,
        season_start: market.season_start, season_end: market.season_end,
        season_year_round: market.season_year_round, hours: market.hours,
        hours_event_based: market.hours_event_based,
        instagram: market.instagram, facebook: market.facebook, tiktok: market.tiktok, logo: market.logo,
        description: market.description,
      };
      if (isNew) {
        await axios.post(`${API}/admin/market`, updateData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Market created");
        navigate("/admin/dashboard?section=markets");
      } else {
        await axios.put(`${API}/admin/market/${marketId}`, { ...updateData, profile_review_needed: false }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Market updated");
        setHasChanges(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const refresh = useCallback(async () => {
    const r = await axios.get(`${API}/admin/markets`, { headers: { Authorization: `Bearer ${token}` } });
    const found = r.data.find((m) => m.id === marketId);
    if (found) setMarket(found);
  }, [marketId, token]);

  const handleApprove = async () => {
    setBusy(true);
    try { await axios.post(`${API}/admin/market/${marketId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Market approved"); await refresh(); }
    catch { toast.error("Failed to approve"); } finally { setBusy(false); }
  };

  const handleReject = async () => {
    setBusy(true);
    try { await axios.post(`${API}/admin/market/${marketId}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Market rejected"); await refresh(); }
    catch { toast.error("Failed to reject"); } finally { setBusy(false); }
  };

  const handleDelistToggle = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/market/${marketId}/${market.is_delisted ? "relist" : "delist"}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(market.is_delisted ? "Relisted" : "Delisted");
      await refresh();
    } catch { toast.error("Failed to toggle delist"); }
    finally { setBusy(false); }
  };

  const handleOverrideToggle = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/market/${marketId}/toggle-override`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Override toggled");
      await refresh();
    } catch { toast.error("Failed to toggle override"); }
    finally { setBusy(false); }
  };

  const handleMarkReviewed = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/market/${marketId}`, { profile_review_needed: false }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Profile edits marked reviewed");
      await refresh();
    } catch {
      toast.error("Failed to mark reviewed");
    } finally { setBusy(false); }
  };

  if (loading || !market) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-[#1a1a1a] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-[#1a1a1a]" /></div>
            <div>
              <span className="font-heading text-lg font-semibold">Admin Portal</span>
              <p className="text-xs text-gray-400">614 Shop Local</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Button variant="ghost" onClick={() => navigate("/admin/dashboard?section=markets")} className="mb-6 text-slate-600 hover:text-slate-900 -ml-2" data-testid="admin-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">{isNew ? "Add New Market" : "Edit Market"}</h1>
            <p className="text-slate-500 mt-1">{isNew ? "Create a new market listing" : market.market_name || market.email}</p>
          </div>
          <Button onClick={handleSave} disabled={saving || (!isNew && !hasChanges)} className="h-10 px-6 rounded-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] font-bold disabled:opacity-50" data-testid="admin-save-btn" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              : (isNew || hasChanges) ? <><Save className="w-4 h-4 mr-2" /> Save Changes</>
              : <><CheckCircle className="w-4 h-4 mr-2" /> Saved</>}
          </Button>
        </div>

        <MarketProfileForm
          data={market}
          onChange={handleChange}
          token={token}
          uploadEndpoint="/api/admin/upload/logo"
          emailReadOnly={false}
          suffixSlot={!isNew ? (
            <AdminStatusCard
              data={market}
              busy={busy}
              approvalKey="approval_status"
              onApprove={handleApprove}
              onReject={handleReject}
              onDelistToggle={handleDelistToggle}
              onOverrideToggle={handleOverrideToggle}
              onMarkReviewed={handleMarkReviewed}
            />
          ) : null}
        />

        {!isNew && (
          <Card className="mt-6 bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="admin-market-vendors-card">
            <CardHeader>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="w-5 h-5 text-[#42D78A]" /> Market Vendors
                </CardTitle>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button
                    type="button"
                    onClick={openAddVendorModal}
                    className="rounded-full bg-[#42D78A] px-5 font-bold text-[#1a1a1a] hover:bg-[#3bc27c]"
                    data-testid="admin-open-add-vendor-modal"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add Vendor
                  </Button>
                  <div className="flex w-fit flex-wrap rounded-full bg-gray-100 p-1">
                    {[
                      ["pending", "Pending", vendorRequestCounts.pending],
                      ["approved", "Approved", vendorRequestCounts.approved],
                      ["rejected", "Not Approved", vendorRequestCounts.rejected],
                    ].map(([status, label, count]) => (
                      <button
                        key={status}
                        type="button"
                        onClick={() => setVendorRequestStatus(status)}
                        className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${vendorRequestStatus === status ? "bg-[#1a1a1a] text-white" : "text-slate-600 hover:text-[#1a1a1a]"}`}
                        data-testid={`admin-vendor-requests-${status}-tab`}
                      >
                        {label}
                        <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${vendorRequestStatus === status ? "bg-white text-[#1a1a1a]" : "bg-white text-slate-500"}`}>{count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {filteredVendorRequests.length === 0 ? (
                <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                  No {vendorRequestStatus === "rejected" ? "not approved" : vendorRequestStatus} vendors for this market yet.
                </p>
              ) : filteredVendorRequests.map((request) => (
                <div key={`${request.business_id}-${request.requested_at || request.status}`} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <button
                      type="button"
                      onClick={() => setSelectedVendorBusiness(request)}
                      className="min-w-0 flex-1 text-left"
                      data-testid={`admin-view-vendor-business-card-${request.business_id}`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{request.business_name}</p>
                        <Badge className={request.status === "approved" ? "bg-emerald-100 text-emerald-800" : request.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}>
                          {request.status === "approved" ? "Approved" : request.status === "rejected" ? "Not Approved" : "Pending"}
                        </Badge>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">{request.business_email}</p>
                      {(request.business_type || request.business_categories?.length > 0) && (
                        <p className="mt-2 text-sm text-slate-600">
                          {[request.business_type, ...(request.business_categories || [])].filter(Boolean).join(" · ")}
                        </p>
                      )}
                      {request.business_description && (
                        <p className="mt-2 line-clamp-2 text-sm text-slate-500">{request.business_description}</p>
                      )}
                    </button>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedVendorBusiness(request)}
                        className="rounded-full bg-white text-slate-700 hover:bg-slate-100"
                        data-testid={`admin-view-vendor-business-${request.business_id}`}
                      >
                        <Eye className="mr-1 h-4 w-4" /> View Profile
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handleVendorRequestAction(request.business_id, "approved")}
                        disabled={updatingVendorRequest.startsWith(request.business_id)}
                        className="rounded-full bg-[#42D78A] text-[#1a1a1a] hover:bg-[#3bc27c]"
                        data-testid={`admin-approve-vendor-${request.business_id}`}
                      >
                        {updatingVendorRequest === `${request.business_id}-approved` ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-1 h-4 w-4" />} Approve
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleVendorRequestAction(request.business_id, "rejected")}
                        disabled={updatingVendorRequest.startsWith(request.business_id)}
                        className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        data-testid={`admin-reject-vendor-${request.business_id}`}
                      >
                        {updatingVendorRequest === `${request.business_id}-rejected` ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <XCircle className="mr-1 h-4 w-4" />} Not Approve
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Dialog open={showAddVendorModal} onOpenChange={setShowAddVendorModal}>
          <DialogContent className="max-h-[88vh] max-w-5xl overflow-y-auto rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Add Vendor</DialogTitle>
              <DialogDescription>
                Search approved businesses and add them to {market.market_name || "this market"}.
              </DialogDescription>
            </DialogHeader>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <Input
                value={vendorSearchTerm}
                onChange={(event) => setVendorSearchTerm(event.target.value)}
                placeholder="Search business names, categories, or locations..."
                className="h-12 rounded-full pl-12"
                data-testid="admin-add-vendor-search"
              />
            </div>
            {loadingAvailableVendors ? (
              <div className="flex items-center justify-center rounded-xl bg-slate-50 p-8 text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading businesses
              </div>
            ) : filteredAvailableVendors.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
                No businesses found.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {filteredAvailableVendors.map((business) => (
                  <VendorBusinessCard
                    key={vendorIdentifier(business)}
                    business={business}
                    adding={addingVendorId === vendorIdentifier(business)}
                    onView={() => setSelectedVendorBusiness(business)}
                    onAdd={() => handleAddVendor(business)}
                  />
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
        <BusinessDetailModal
          business={selectedVendorBusiness}
          open={!!selectedVendorBusiness}
          onClose={() => setSelectedVendorBusiness(null)}
          isSaved={false}
          onFollow={() => {}}
        />
      </main>
    </div>
  );
};

const VendorBusinessCard = ({ business, adding, onView, onAdd }) => {
  const logoUrl = getLogoUrl(business.logo);
  const identifier = vendorIdentifier(business);
  const status = business.vendor_status;
  const isAdded = status === "approved";
  const isPending = status === "pending";
  const disabled = adding || isAdded || isPending || !identifier;
  const statusLabel = isAdded ? "Added" : isPending ? "Pending" : "Add";

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onView}
          className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-100"
          data-testid={`admin-view-add-vendor-business-${identifier || "missing"}`}
        >
          {logoUrl ? (
            <img src={logoUrl} alt={business.business_name || "Business logo"} className="h-full w-full object-cover" />
          ) : (
            <span className="text-2xl font-black text-slate-400" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {(business.business_name || "B").slice(0, 1).toUpperCase()}
            </span>
          )}
        </button>
        <div className="min-w-0 flex-1">
          <button type="button" onClick={onView} className="block w-full text-left">
            <p className="truncate font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{business.business_name || "Unnamed Business"}</p>
            {business.business_type && <p className="mt-1 text-sm text-slate-500">{business.business_type}</p>}
            {toSearchableList(business.business_locations).length > 0 && (
              <p className="mt-1 truncate text-sm text-slate-500">{toSearchableList(business.business_locations).join(", ")}</p>
            )}
          </button>
          {business.business_description && (
            <p className="mt-2 line-clamp-2 text-sm text-slate-600">{business.business_description}</p>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onView}
          className="rounded-full bg-white"
        >
          <Eye className="mr-1.5 h-4 w-4" /> View
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={onAdd}
          disabled={disabled}
          className={isAdded || isPending ? "rounded-full bg-slate-100 text-slate-500 hover:bg-slate-100" : "rounded-full bg-[#42D78A] text-[#1a1a1a] hover:bg-[#3bc27c]"}
          data-testid={`admin-add-vendor-${identifier || "missing"}`}
        >
          {adding ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : isAdded ? <CheckCircle className="mr-1.5 h-4 w-4" /> : <Plus className="mr-1.5 h-4 w-4" />}
          {statusLabel}
        </Button>
      </div>
    </div>
  );
};

export default AdminMarketEditPage;
